'use client';

import { useEffect, useState, useRef, useMemo, useImperativeHandle, useCallback } from 'react';
import { Mail, X, Send, ChevronDown, LinkedinIcon, Link2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import Link from 'next/link';
import { useChat } from 'ai/react';
import {
	AIEmail,
	AIEmailStatus,
	AILinkedInInvite,
	AILinkedInInviteStatus,
	AIStrategy,
	AIUpload,
	AIUploadStatus,
	Email,
	EmailDomain
} from '@prisma/client';
import AIEmailSuggestion from '@/components/ui/ai-email-suggestion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import TestEmailDialog from '@/components/ui/test-email-dialog';
import { EmailSendModal } from '@/components/ui/email-send-modal';
import { LoadingSpinner } from '@/components/loading-indicator';
import { useRouter } from 'next/navigation';
import { StrategyAIEmail, StrategyAILinkedinInvite } from 'types/extend-prisma';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/lib/react-utils';
import { extractNameFromEmail } from '@/lib/utils';
import AIInviteSuggestion from '@/components/ui/ai-invite-suggestion';

import { forwardRef } from 'react';
import { Session } from 'next-auth';

const HIGH_INTENT_THRESHOLD = 5;

interface EmailStrategyAreaProps {
	onEmailStatusChange?: (status: string) => void;
	domainParam?: string | null;
	session: Session | null;
}

// Define ref handle interface
export interface EmailStrategyAreaHandle {
	handleFetchingStrategyExecutionStreams?: (() => Promise<void>) | undefined;
}

interface LoadingCompany {
	key: string;
	title?: string | undefined;
	domain?: string | undefined;
	relevance?: number | undefined;
	role_relevance?: number | undefined;
	email?: string | undefined;
	description?: string | undefined;
	extra_description?: string | undefined;
	aiUpload?: AIUpload | undefined;
	summary?: string | undefined;
}

const EmailStrategy = forwardRef<EmailStrategyAreaHandle, EmailStrategyAreaProps>(
	({ onEmailStatusChange, domainParam, session }, ref) => {
		const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
		const [expandedEmail, setExpandedEmail] = useState<string | null>(null);
		const [aiEmails, setAiEmails] = useState<StrategyAIEmail[]>([]);
		const [aiLinkedinInvites, setAiLinkedinInvites] = useState<StrategyAILinkedinInvite[]>([]);
		const [hostname, setHostname] = useState('');
		const [invite, setInvite] = useState<AILinkedInInvite | StrategyAILinkedinInvite | null>(
			null
		);
		const isMobile = useIsMobile(); // Use the custom hook
		const [strategy, setStrategy] = useState<AIStrategy | null>(null);
		const [isInitializing, setIsInitializing] = useState<boolean>(true);
		const [isLoading, setIsLoading] = useState<boolean>(false);
		const [domains, setDomains] = useState<EmailDomain[]>([]);
		const [companies, setCompanies] = useState<LoadingCompany[]>([]);
		const refCompaniesContainer = useRef<HTMLDivElement | null>(null);

		const hasSentHighIntentRef = useRef(true);

		const [error, setError] = useState<string>('');
		const refSiteId = useRef<string | null>(null);

		const router = useRouter();

		const fetchUserProfile = async (userId: string) => {
			try {
				const response = await fetch(`/api/sites/${refSiteId.current}/users/${userId}`);
				if (!response.ok) {
					throw new Error('Failed to fetch user profile');
				}
				const userProfile = await response.json();

				if (userProfile.signature) {
					setEmailSignature(userProfile.signature);
				}
			} catch (err: any) {
				console.error('Error fetching user profile:', err.message);
			}
		};

		useEffect(() => {
			if (!session || !session?.siteId) {
				return;
			}
			refSiteId.current = session.siteId;
			// if (!session?.user?.email) {
			// 	router.push('/signup');
			// 	return;
			// }
			fetchStrategy(session.siteId);
			fetchAIEmails();
			fetchAILinkedinInvites();
			fetchDomains(session.siteId);
			if (session?.user?.email) {
				// Already a customer so don't send high intent
				try {
					const fullName = extractNameFromEmail(session.user.email);
					if (fullName && fullName.firstName) {
						setEmailName(fullName.firstName);
					}
				} catch (e) {}

				// Fetch user profile to get signature and name
				if (session?.user?.id) {
					fetchUserProfile(session.user.id);
				}
			}
		}, [session]);

		useEffect(() => {
			if (typeof window !== 'undefined') {
				setHostname(window.location.hostname);
			}
		}, []);

		const [isEmailing, setIsEmailing] = useState(false);
		const [emailName, setEmailName] = useState<string>('');
		const [emailDomain, setEmailDomain] = useState<string>('');
		const [emailSignature, setEmailSignature] = useState<string>('');
		// const [isNeedLogin, setIsNeedLogin] = useState<boolean>(false);

		const [isTestEmailOpen, setIsTestEmailOpen] = useState<boolean>(false);
		const [testEmailError, setTestEmailError] = useState<string | null>('');
		const [testAIEmail, setTestAIEmail] = useState<AIEmail | null>(null);
		const [isRunning, setIsRunning] = useState<boolean>(false);
		const [sentHighintent, setSentHighIntent] = useState<boolean>(false);
		const [isEmailSendModalOpen, setIsEmailSendModalOpen] = useState<boolean>(false);
		const [shouldAutoOpenEmailModal, setShouldAutoOpenEmailModal] = useState<boolean>(false);
		const [hasShownHighIntentModal, setHasShownHighIntentModal] = useState<boolean>(false);

		// Add a dropdown for sorting emails by createdAt or updatedAt
		const [sortOption, setSortOption] = useState<'createdAt'>('createdAt');
		const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

		const fetchStrategy = async (siteId: string) => {
			setIsLoading(true);
			try {
				const response = await fetch(`/api/sites/${refSiteId.current}/ai-strategies`);
				setIsLoading(false);
				if (!response.ok) {
					throw new Error('Failed to fetch site');
				}
				const data = await response.json();
				if (!data) {
					router.push(`/`);
					return;
				}
				setStrategy(data);
				setIsInitializing(false);
				return data;
			} catch (err: any) {
				setIsLoading(false);
				setIsInitializing(false);
				setError(err.message);
			}
		};

		useEffect(() => {
			if (!strategy) return;
			if (!session?.user) return;
			console.log(`Strategy is running: ${strategy.isRunning} and isRunning: ${isRunning}`);
			if (strategy.isRunning && !isRunning) {
				setIsRunning(true);
				// Should fetch stream
				handleFetchingStrategyExecutionStreams();
			}
		}, [strategy, session]);

		const fetchDomains = async (siteId: string) => {
			try {
				const response = await fetch(`/api/sites/${siteId}/domains/for-sending`);
				if (!response.ok) {
					throw new Error('Failed to fetch site');
				}
				const data = await response.json();
				if (data.length > 0) {
					setDomains(data);
					setEmailDomain(data[0].domain);
				} else {
					setEmailDomain('oxide.email');
				}
			} catch (err: any) {}
		};

		const fetchAIEmails = async () => {
			try {
				const response = await fetch(
					`/api/sites/${refSiteId.current}/ai-strategies/emails`
				);
				if (!response.ok) {
					throw new Error('Failed to fetch emails');
				}
				const data = await response.json();
				console.log(data);
				setAiEmails(data);
				return data;
			} catch (err: any) {
				setError(err.message);
			}
		};

		const fetchAILinkedinInvites = async () => {
			try {
				const response = await fetch(
					`/api/sites/${refSiteId.current}/ai-strategies/invites`
				);
				if (!response.ok) {
					throw new Error('Failed to fetch invites');
				}
				const data = await response.json();
				const invites = [];
				for (const invite of data) {
					// Change linkedin url to normal url format, so if it doesn't start with https://www.linkedin.com/in/ we need to transform it
					if (!invite.linkedinUrl.includes('linkedin.com/in/')) {
						invite.linkedinUrl = `https://www.linkedin.com/in/${invite.linkedinUrl}`;
					} else if (invite.linkedinUrl.includes('linkedin.com/in/')) {
						invite.linkedinUrl = `https://www.linkedin.com/in/${invite.linkedinUrl.split('/in/').pop()}`;
					} else {
						// Unexpected linkedin url format
						console.error('Unexpected LinkedIn URL format', invite.linkedinUrl);
						continue;
					}
					invites.push(invite);
				}
				setAiLinkedinInvites(invites);
				return invites;
			} catch (err: any) {
				console.error(err);
				// setError(err.message);
			}
		};

		const {
			handleSubmit: getStrategyExecutionStreams,
			isLoading: isExecutingStrategy,
			data: strategyExecutionStreams
		} = useChat({
			api: `/api/sites/${refSiteId.current}/ai-strategies/stream`,
			onResponse: (res) => console.log('Executing Strategy Response started:', res),
			onFinish: (msg) => console.log('Executing Strategy finished. Final AI message:', msg),
			onError(error) {
				console.error('Error streaming:', error);
			},
			credentials: 'include'
		});

		interface StreamStrategyObject {
			action: 'create' | 'update' | 'delete';
			modelName: 'ai_uploads' | 'ai_emails' | 'ai_strategies' | 'emails';
			created?: AIUpload | AIEmail | AIStrategy | Email | undefined;
			after?: AIUpload | AIEmail | AIStrategy | Email | undefined;
		}

		const handleFetchingStrategyExecutionStreams = useCallback(async () => {
			console.log('Fetching strategy execution streams');
			if (isExecutingStrategy) {
				console.warn(
					`handleFetchingStrategyExecutionStreams: Strategy execution streams is already loading`
				);
				return;
			}
			if (!session?.user?.email) {
				// If not user and strategy execution fetches - then flag hasSentHighIntentRef for triggerable
				hasSentHighIntentRef.current = false;
			}
			getStrategyExecutionStreams(new Event('click'), {
				body: {},
				// Missing the following property will cause the request to be blocked
				allowEmptySubmit: true
			});
		}, [strategy, session, refSiteId]);

		// Expose methods via useImperativeHandle
		useImperativeHandle(ref, () => {
			if (!refSiteId.current) {
				console.warn('Site ID is not set');
				return {
					handleFetchingStrategyExecutionStreams: undefined
				};
			}
			if (!session?.user) {
				console.warn('User is not logged in');
				return {
					handleFetchingStrategyExecutionStreams: undefined
				};
			}
			if (!strategy) {
				console.warn('Strategy is not set');
				return {
					handleFetchingStrategyExecutionStreams: undefined
				};
			}
			console.log(
				`Setting up ref handle for EmailStrategyArea: siteId:${refSiteId?.current}`
			);
			return {
				handleFetchingStrategyExecutionStreams: handleFetchingStrategyExecutionStreams
			};
		}, [handleFetchingStrategyExecutionStreams, session, refSiteId, strategy]);

		useEffect(() => {
			console.log('Strategy execution streams:', strategyExecutionStreams);
			if (!strategyExecutionStreams) {
				return;
			}
			if (strategyExecutionStreams.length === 0) {
				return;
			}
			// Take only the most recent stream
			const stream = strategyExecutionStreams.pop();
			const streamAction = stream as unknown as StreamStrategyObject;
			console.log(`Stream action:`, streamAction);
			if (streamAction.action === 'create' && streamAction.modelName === 'ai_emails') {
				// Check if it's already in the list
				if (aiEmails.find((email) => email.id === streamAction.created?.id)) {
					return;
				}
				const _aiEmail: AIEmail = streamAction.created as unknown as AIEmail;
				// setAiEmails([...aiEmails, _aiEmail]);

				setAiEmails((prev) => {
					if (prev.some((e) => e.id === _aiEmail.id)) return prev;
					const next = [...prev, _aiEmail];
					// only fire when we go 5→6, never on 6→7
					// fire exactly when you hit 5 emails
					if (!hasSentHighIntentRef.current && next.length === HIGH_INTENT_THRESHOLD) {
						hasSentHighIntentRef.current = true;
						submitHighIntent();
					}
					return next;
				});

				// Find and remove companies item where domain matches
				const createdEmailDomain = _aiEmail.email.split('@').pop();
				if (!createdEmailDomain) {
					return;
				}
				console.log(`Removing company with domain ${createdEmailDomain}`);
				setCompanies((companies) =>
					companies.filter((company) => company.domain !== createdEmailDomain)
				);
			}
			if (streamAction.action === 'create' && streamAction.modelName === 'emails') {
				const _email: Email = streamAction.created as unknown as Email;
				if (aiEmails.find((email) => email.email === _email.email)) {
					return;
				}

				// Find companies item where domain matches
				const createdEmailDomain = _email.email.split('@').pop();
				if (!createdEmailDomain) {
					return;
				}
				const company = companies.find((company) => company.domain === createdEmailDomain);
				if (!company) {
					const newCompany: LoadingCompany = {
						key: createdEmailDomain,
						domain: createdEmailDomain,
						email: _email.email,
						summary: _email.summary ?? '',
						role_relevance: _email.role_relevance ?? 0,
						title: _email.title ?? '',
						relevance: _email.company_relevance ?? 0,
						description: _email.company_description ?? '',
						extra_description: _email.company_extra_description ?? ''
					};
					setCompanies((companies) => [...companies, newCompany]);
					if (companies.length === 0) {
						setTimeout(() => {
							let scrollDownAmount = 600;
							if (refCompaniesContainer.current) {
								// Get the element's bounding rectangle relative to the viewport.
								const rect = refCompaniesContainer.current.getBoundingClientRect();
								// Compute its absolute position on the page.
								const absoluteTop = window.pageYOffset + rect.top;
								scrollDownAmount = absoluteTop;
								console.log(`Scrolling to ${scrollDownAmount}`);
							}
							if (aiEmails.length === 0) {
								window.scrollBy({
									top: 600, // adjust this value as needed
									behavior: 'smooth'
								});
							} else {
								window.scrollBy({
									top: scrollDownAmount, // adjust this value as needed
									behavior: 'smooth'
								});
							}
						}, 0);
					}
					return;
				}
				console.log(`Adding email ${_email.email} to company ${company.title}`);
				company.email = _email.email;
				company.summary = _email.summary ?? '';
				company.role_relevance = _email.role_relevance ?? 0;
			}
			if (streamAction.modelName === 'ai_uploads') {
				if (streamAction.action === 'create') {
					const _aiUpload: AIUpload = streamAction.created as unknown as AIUpload;
					if (onEmailStatusChange) {
						onEmailStatusChange(
							`Finding leads for ${_aiUpload.prompt}` ?? 'Finding leads'
						);
						if (companies.length === 0) {
							setTimeout(() => {
								let scrollDownAmount = 600;
								if (refCompaniesContainer.current) {
									// Get the element's bounding rectangle relative to the viewport.
									const rect =
										refCompaniesContainer.current.getBoundingClientRect();
									// Compute its absolute position on the page.
									const absoluteTop = window.pageYOffset + rect.top;
									scrollDownAmount = absoluteTop;
									console.log(`Scrolling to ${scrollDownAmount}`);
								}
								if (aiEmails.length === 0) {
									window.scrollBy({
										top: 600, // adjust this value as needed
										behavior: 'smooth'
									});
								} else {
									window.scrollBy({
										top: scrollDownAmount, // adjust this value as needed
										behavior: 'smooth'
									});
								}
							}, 0);
						}
					}
					if (!_aiUpload.companies) {
						return;
					}

					// Add companies to the list if they are not already there
					// Check if company.domain is already in the list
					const _companies = _aiUpload.companies
						.map((companyJson) => {
							const company = companyJson as unknown as LoadingCompany;
							if (
								companies.find(
									(existingCompany) => existingCompany.domain === company.domain
								)
							) {
								return null;
							}
							return {
								key: company.domain,
								title: company.title,
								domain: company.domain,
								relevance: company.relevance,
								description: company.description,
								extra_description: company.extra_description,
								aiUpload: _aiUpload
							};
						})
						.filter((company) => company !== null) as LoadingCompany[];
					console.log(`Adding ${_companies.length} new companies`);
					setCompanies((companies) => [...companies, ..._companies]);
				} else if (streamAction.action === 'update') {
					const _aiUpload: AIUpload = streamAction.after as unknown as AIUpload;
					if (_aiUpload.status === AIUploadStatus.PENDING) {
						return;
					}
					if (_aiUpload.status === AIUploadStatus.COMPLETED) {
						if (onEmailStatusChange) {
							onEmailStatusChange(`Leads found for ${_aiUpload.prompt}`);
						}
					} else if (_aiUpload.status === AIUploadStatus.FAILED) {
						if (onEmailStatusChange) {
							onEmailStatusChange(`Failed to find leads for ${_aiUpload.prompt}`);
						}
					}

					// Remove companies that match aiUpload.id that does not have email
					setCompanies((companies) => [
						...companies.filter(
							(company) => company.aiUpload?.id !== _aiUpload.id || company.email
						)
					]);
				}
			}
			if (streamAction.modelName === 'ai_strategies') {
				if (streamAction.action === 'update') {
					const _strategy: AIStrategy = streamAction.after as unknown as AIStrategy;
					if (_strategy.isRunning) {
						console.log('Strategy is starting');
						if (onEmailStatusChange) {
							onEmailStatusChange('Starting strategy execution...');
						}
						setIsRunning(true);
					} else {
						if (onEmailStatusChange) {
							onEmailStatusChange('Strategy execution completed');
							if (aiEmails.length === 0) {
								// Something is really wrong
								fetchAIEmails();
							}
						}
						setIsRunning(false);
						setCompanies([]);
					}
				}
			}
		}, [strategyExecutionStreams]);
		// End of strategy execution streams

		// Utility function to batch promises
		const batchExecute = async (
			functions: Array<() => Promise<Response>>,
			batchSize: number,
			delayMs: number
		) => {
			for (let i = 0; i < functions.length; i += batchSize) {
				const chunk = functions.slice(i, i + batchSize);
				console.log(
					`Sending batch ${i / batchSize + 1} of ${Math.ceil(functions.length / batchSize)} chunk size of ${chunk.length}`
				);
				// Trigger each request now
				await Promise.all(chunk.map((fn) => fn()));
				if (i + batchSize < functions.length) {
					// Wait before next batch
					await new Promise((resolve) => setTimeout(resolve, delayMs));
				}
			}
		};

		const submitHighIntent = async () => {
			fetch(`/api/sites/${refSiteId.current}/high-intent`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					companies_count: companies?.length ?? 0,
					domain: domainParam ?? ''
				})
			});

			// Check if we should auto-open the email modal at this milestone
			if (!hasShownHighIntentModal) {
				// Wait a bit to allow auto-generation to complete
				setTimeout(() => {
					const hasDomain = domains.length > 0;
					const isConfigured = emailName.trim() && emailSignature.trim() && hasDomain;

					// Only auto-open if we're missing configuration
					if (!isConfigured && !isEmailSendModalOpen) {
						setShouldAutoOpenEmailModal(true);
						setIsEmailSendModalOpen(true);
						setHasShownHighIntentModal(true);
					}
				}, 500);
			}
		};

		const handleSendClick = (isSelected: boolean = false) => {
			setShouldAutoOpenEmailModal(false); // Don't show auto-open styling
			setIsEmailSendModalOpen(true);
		};

		const sendAllEmails = async (isSelected: boolean = false): Promise<void> => {
			console.log('Sending all emails');

			if (!session?.user?.email) {
				router.push('/signup');
				return;
			}

			setIsEmailing(true);

			// Arrays for immediate PUT requests
			const putPromises: Promise<Response>[] = [];

			// Arrays for queued send requests as functions (not executed yet)
			const sendRequestFunctions: Array<() => Promise<Response>> = [];

			let i = 0;
			let _aiEmails = [...aiEmails];
			let _aiLinkedinInvites = [...aiLinkedinInvites];
			let _selectedEmails = [...selectedEmails];

			// Process AI Emails
			for (const aiEmail of aiEmails) {
				if (isSelected && !_selectedEmails.includes(aiEmail.id)) {
					continue;
				}

				if (aiEmail.status === AIEmailStatus.PENDING) {
					aiEmail.status = AIEmailStatus.ACCEPTED;

					// Remove from arrays
					_aiEmails = _aiEmails.filter((item) => item.id !== aiEmail.id);
					_selectedEmails = _selectedEmails.filter((item) => item !== aiEmail.id);

					// PUT request to update status
					putPromises.push(
						fetch(`/api/sites/${refSiteId.current}/ai-emails/${aiEmail.id}`, {
							method: 'PUT',
							headers: {
								'Content-Type': 'application/json'
							},
							body: JSON.stringify({ status: AIEmailStatus.ACCEPTED })
						})
					);

					// Store a function that, when called, sends the queue-send POST request
					sendRequestFunctions.push(() =>
						fetch(
							`/api/sites/${refSiteId.current}/ai-emails/${aiEmail.id}/queue-send`,
							{
								method: 'POST',
								headers: { 'Content-Type': 'application/json' },
								body: JSON.stringify({
									subject: aiEmail.subject,
									body: aiEmail.body,
									email: aiEmail.email,
									name: emailName,
									domain: emailDomain,
									signature: emailSignature,
									delay: i * 20
								})
							}
						)
					);

					i++;
				}
			}

			// Process LinkedIn Invites
			for (const aiInvite of aiLinkedinInvites) {
				if (isSelected && !_selectedEmails.includes(aiInvite.id)) {
					continue;
				}

				if (aiInvite.status === AILinkedInInviteStatus.PENDING) {
					aiInvite.status = AILinkedInInviteStatus.ACCEPTED;

					_aiLinkedinInvites = _aiLinkedinInvites.filter(
						(item) => item.id !== aiInvite.id
					);
					_selectedEmails = _selectedEmails.filter((item) => item !== aiInvite.id);

					// PUT request to update to QUEUED
					putPromises.push(
						fetch(
							`/api/sites/${refSiteId.current}/aiuploads/${aiInvite.aiUploadId}/ailinkedin-invites/${aiInvite.id}`,
							{
								method: 'PUT',
								headers: {
									'Content-Type': 'application/json'
								},
								body: JSON.stringify({
									status: AILinkedInInviteStatus.QUEUED
								})
							}
						)
					);

					// Store a function for the send request
					sendRequestFunctions.push(() =>
						fetch(
							`/api/sites/${refSiteId.current}/aiuploads/${aiInvite.aiUploadId}/ailinkedin-invites/${aiInvite.id}/send`,
							{
								method: 'POST',
								headers: { 'Content-Type': 'application/json' },
								body: JSON.stringify({
									subject: aiInvite.subject,
									body: aiInvite.body,
									email: aiInvite.email,
									linkedinUrl: aiInvite.linkedinUrl,
									name: aiInvite.name,
									signature: emailSignature
								})
							}
						)
					);
				}
			}

			// Wait for all PUT requests to complete
			setIsInitializing(true);
			if (putPromises.length > 0) {
				await Promise.all(putPromises);
			}

			// Now execute the queue-send requests in batches of 50 per second
			if (sendRequestFunctions.length > 0) {
				await batchExecute(sendRequestFunctions, 50, 2000);
			}

			setIsInitializing(false);

			// Update state
			setAiEmails(_aiEmails);
			setSelectedEmails(_selectedEmails);
			setAiLinkedinInvites(_aiLinkedinInvites);

			setIsEmailing(false);
		};

		const toggleEmailSelection = (id: string) => {
			setSelectedEmails((prev) =>
				prev.includes(id) ? prev.filter((emailId) => emailId !== id) : [...prev, id]
			);
		};

		const validateEmail = (email: string) => {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			return emailRegex.test(email);
		};

		const handleDiscard = async (aiEmail: AIEmail, shouldSetAIEmails: boolean = true) => {
			try {
				// TODO: Make it backend later
				aiEmail.status = AIEmailStatus.REJECTED;
				const copyOfAIEmail = JSON.parse(JSON.stringify(aiEmail));
				delete copyOfAIEmail.aiUpload;
				delete copyOfAIEmail.campaign;
				const response = await fetch(
					`/api/sites/${refSiteId.current}/ai-emails/${aiEmail.id}`,
					{
						method: 'PUT',
						headers: {
							'Content-Type': 'application/json'
						},
						body: JSON.stringify(copyOfAIEmail)
					}
				);
				if (!response.ok) {
					throw new Error('Failed to discard email');
				}
				if (shouldSetAIEmails) {
					setAiEmails(aiEmails.filter((item) => item.id !== aiEmail.id));
				}
			} catch (err: any) {
				setError(err.message);
			}
		};

		const handleInviteDiscard = async (
			aiInvite: AILinkedInInvite,
			shouldSetAIEmails: boolean = true
		) => {
			try {
				aiInvite.status = AILinkedInInviteStatus.REJECTED;
				const copyOfAIInvite = JSON.parse(JSON.stringify(aiInvite));
				delete copyOfAIInvite.aiUpload;
				delete copyOfAIInvite.campaign;
				const response = await fetch(
					`/api/sites/${refSiteId.current}/aiuploads/${aiInvite.aiUploadId}/ailinkedin-invites/${aiInvite.id}`,
					{
						method: 'PUT',
						headers: {
							'Content-Type': 'application/json'
						},
						body: JSON.stringify(copyOfAIInvite)
					}
				);
				if (!response.ok) {
					throw new Error('Failed to discard invite');
				}
				if (shouldSetAIEmails) {
					setAiLinkedinInvites(
						aiLinkedinInvites.filter((item) => item.id !== aiInvite.id)
					);
				}
			} catch (err: any) {
				setError(err.message);
			}
		};

		const discardSelected = async () => {
			const selectedEmailsCopy = [...selectedEmails];
			setAiEmails(aiEmails.filter((item) => !selectedEmailsCopy.includes(item.id)));
			setAiLinkedinInvites(
				aiLinkedinInvites.filter((item) => !selectedEmailsCopy.includes(item.id))
			);
			setSelectedEmails([]);
			const promisesUpdate = [];
			for (const aiEmail of aiEmails) {
				if (selectedEmailsCopy.includes(aiEmail.id)) {
					promisesUpdate.push(handleDiscard(aiEmail, false));
				}
			}
			for (const aiInvite of aiLinkedinInvites) {
				if (selectedEmailsCopy.includes(aiInvite.id)) {
					promisesUpdate.push(handleInviteDiscard(aiInvite, false));
				}
			}
			await Promise.all(promisesUpdate);
		};

		const discardAll = async () => {
			const allEmails = [...aiEmails];
			const allInvites = [...aiLinkedinInvites];

			// Clear the UI immediately
			setAiEmails([]);
			setAiLinkedinInvites([]);
			setSelectedEmails([]);

			// Update backend
			const promisesUpdate = [];
			for (const aiEmail of allEmails) {
				promisesUpdate.push(handleDiscard(aiEmail, false));
			}
			for (const aiInvite of allInvites) {
				promisesUpdate.push(handleInviteDiscard(aiInvite, false));
			}
			await Promise.all(promisesUpdate);
		};

		const setAIEmail = (aiEmail: StrategyAIEmail) => {
			setAiEmails(aiEmails.map((email) => (email.id === aiEmail.id ? aiEmail : email)));
		};

		const emailingTestCustomer = async (testEmailAddress: string) => {
			if (!session?.user?.email) {
				router.push('/signup');
				return;
			}
			const aiEmail = testAIEmail;
			if (!aiEmail) return;

			if (!validateEmail(testEmailAddress)) {
				setTestEmailError('Please enter a valid email address');
				return;
			}
			const testEmail = testEmailAddress;
			if (!aiEmail.body || !aiEmail.subject || !emailName) {
				setTestEmailError('Please enter a subject and body');
				return;
			}
			setTestEmailError('');
			setIsEmailing(true);
			try {
				aiEmail.status = AIEmailStatus.ACCEPTED;
				const response = await fetch(
					`/api/sites/${refSiteId.current}/ai-emails/${aiEmail.id}/send`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({
							subject: aiEmail.subject,
							body: aiEmail.body,
							email: aiEmail.email,
							name: emailName,
							domain: emailDomain,
							signature: emailSignature,
							test: true,
							testEmail: testEmail
						})
					}
				);
				setIsEmailing(false);
				setIsTestEmailOpen(false);
				if (!response.ok) {
					throw new Error('Failed to add disclaimer');
				}
			} catch (err: any) {}
		};

		const isSendDisabled = useMemo(() => {
			if (!emailName) return true;
			return false;
		}, [aiEmails, emailName, emailSignature]);

		// Auto-open email modal when emails are available but not configured
		// This logic has been moved to submitHighIntent to prevent aggressive opening

		const handleSend = async (aiEmail: AIEmail) => {
			console.log(session);
			if (!session?.user?.email) {
				router.push('/signup');
				return;
			}

			try {
				aiEmail.status = AIEmailStatus.ACCEPTED;
				const response = await fetch(
					`/api/sites/${refSiteId.current}/ai-emails/${aiEmail.id}/send`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({
							subject: aiEmail.subject,
							body: aiEmail.body,
							email: aiEmail.email,
							name: emailName,
							domain: emailDomain,
							signature: emailSignature
						})
					}
				);
				if (!response.ok) {
					throw new Error('Failed to add disclaimer');
				}
				setAiEmails(aiEmails.filter((item) => item.id !== aiEmail.id));
			} catch (err: any) {
				setError(err.message);
			}
		};

		const handleInvite = async (aiInvite: AILinkedInInvite) => {
			console.log(session);
			if (!session?.user?.email) {
				router.push('/signup');
				return;
			}

			try {
				aiInvite.status = AILinkedInInviteStatus.ACCEPTED;
				const response = await fetch(
					`/api/sites/${refSiteId.current}/aiuploads/${aiInvite.aiUploadId}/ailinkedin-invites/${aiInvite.id}/send`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({
							subject: aiInvite.subject,
							body: aiInvite.body,
							email: aiInvite.email,
							linkedinUrl: aiInvite.linkedinUrl,
							name: aiInvite.name,
							signature: emailSignature
						})
					}
				);
				if (!response.ok) {
					throw new Error('Failed to add disclaimer');
				}
				setAiLinkedinInvites(aiLinkedinInvites.filter((item) => item.id !== aiInvite.id));
			} catch (err: any) {
				setError(err.message);
			}
		};

		if (isInitializing) return <LoadingSpinner />;
		if (error) return <div>Error: {error}</div>;

		return (
			<div className="w-full container mx-auto py-4 px-0 space-y-6">
				{/* Email Management Section */}
				<div className="w-full">
					<div className="flex flex-wrap items-center justify-between gap-2 mb-5">
						<h2 className="text-xl font-semibold">
							{aiEmails.length && aiLinkedinInvites.length
								? `Email & LinkedIn Review (${aiEmails.length} emails & ${aiLinkedinInvites.length} invites)`
								: `Email Review (${aiEmails.length} emails)`}
						</h2>

						{/* Send and Discard Buttons */}
						<div className="flex gap-2">
							<Button
								variant="outline"
								disabled={
									isEmailing ||
									isLoading ||
									aiEmails.length + aiLinkedinInvites.length === 0
								}
								onClick={() =>
									selectedEmails.length > 0 ? discardSelected() : discardAll()
								}
								className="gap-2 text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
							>
								<X className="w-4 h-4" />
								{selectedEmails.length > 0
									? `Discard Selected (${selectedEmails.length})`
									: 'Discard All'}
							</Button>
							<Button
								variant="default"
								disabled={
									isEmailing ||
									isLoading ||
									aiEmails.length + aiLinkedinInvites.length === 0
								}
								onClick={() => handleSendClick(selectedEmails.length > 0)}
								className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-2"
								size="lg"
							>
								<Send className="w-4 h-4" />
								{selectedEmails.length > 0
									? `Send Selected (${selectedEmails.length})`
									: `Send All (${aiEmails.length + aiLinkedinInvites.length})`}
							</Button>
						</div>
					</div>
					<div className="flex items-center justify-between gap-2 mb-5">
						<div className="flex items-center gap-2">
							<input
								type="checkbox"
								onChange={(e) => {
									if (e.target.checked) {
										const allIds = [
											...aiEmails.map((email) => email.id),
											...aiLinkedinInvites.map((invite) => invite.id)
										];
										setSelectedEmails(allIds);
									} else {
										setSelectedEmails([]);
									}
								}}
								checked={
									selectedEmails.length ===
										aiEmails.length + aiLinkedinInvites.length &&
									selectedEmails.length > 0
								}
								className="h-4 w-4 rounded border-gray-300 sm:h-3 sm:w-3"
							/>
							<span className="text-sm font-medium">Select All</span>
						</div>
						<div className="flex items-center gap-2">
							<select
								value={`${sortOption}-${sortOrder}`}
								onChange={(e) => {
									const [option, order] = e.target.value.split('-');
									const newSortOption = option as 'createdAt';
									const newSortOrder = order as 'asc' | 'desc';

									setSortOption(newSortOption);
									setSortOrder(newSortOrder);

									const sortedEmails = [...aiEmails]
										.filter((email) => email.id)
										.sort((a, b) => {
											const fieldA =
												newSortOption === 'createdAt'
													? new Date(a[newSortOption]!).getTime()
													: a[newSortOption] || 0;
											const fieldB =
												newSortOption === 'createdAt'
													? new Date(b[newSortOption]!).getTime()
													: b[newSortOption] || 0;
											return newSortOrder === 'asc'
												? fieldA - fieldB
												: fieldB - fieldA;
										});

									setAiEmails(sortedEmails);
								}}
								className="h-10 border border-input rounded px-2 py-1"
							>
								<option value="createdAt-desc">Created At (Desc)</option>
								<option value="createdAt-asc">Created At (Asc)</option>
							</select>
						</div>
					</div>
					<Progress value={30} className="h-2 mb-5" />

					<div className="space-y-2">
						{aiLinkedinInvites.map((invite) => (
							<Collapsible
								key={invite.id}
								open={expandedEmail === invite.id}
								onOpenChange={() =>
									setExpandedEmail(expandedEmail === invite.id ? null : invite.id)
								}
							>
								<div
									onClick={(e) => {
										// Get closest target
										const target = e.target as HTMLElement;
										if (target.tagName === 'INPUT') return;
										setExpandedEmail(
											expandedEmail === invite.id ? null : invite.id
										);
									}}
									className="cursor-pointer flex p-4 items-center gap-4 flex-wrap rounded-lg border bg-card hover:bg-accent transition-colors"
								>
									<div className="flex-1">
										<div className="flex items-center gap-2">
											<input
												type="checkbox"
												checked={selectedEmails.includes(invite.id)}
												onChange={() => toggleEmailSelection(invite.id)}
												className="h-4 w-4 rounded border-gray-300 sm:h-3 sm:w-3"
											/>
											{!isMobile && (
												<LinkedinIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
											)}
											<span className="font-medium">
												<strong>
													<Link
														className="text-black-500 underline"
														href={`${invite.linkedinUrl}`}
														target="_blank"
													>
														{invite.linkedinUrl}
													</Link>
												</strong>
												{` `}@
												<Link
													className="text-black-500 underline"
													href={`https://${invite.email.split('@').pop()}`}
													target="_blank"
												>
													{invite.email.split('@').pop()}
												</Link>
											</span>
										</div>
										<h3 className="text-sm font-medium line-clamp-4 md:line-clamp-1">
											{invite.body}
										</h3>
										{invite.aiUpload && !isMobile && (
											<div className="flex gap-2 mt-2">
												<Link
													href={`/ai-uploads/${invite.aiUploadId}`}
													target="_blank"
												>
													<Badge
														variant={'secondary'}
														className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
													>
														<span className="italic mr-1 text-xs sm:text-sm">
															{invite.aiUpload?.rolePrompt} at
														</span>
														<span className="text-xs sm:text-sm">
															{invite.aiUpload?.prompt}
														</span>
													</Badge>
												</Link>
												{invite.company_relevance ? (
													<Badge
														variant={'secondary'}
														className="bg-teal-100 text-teal-700 hover:bg-teal-100"
													>
														<span className="italic mr-1 text-xs sm:text-sm">
															Search Score:
														</span>
														<span className="text-xs sm:text-sm">
															{invite.company_relevance}
														</span>
													</Badge>
												) : null}
												<Badge
													variant={'secondary'}
													className="bg-gray-100 text-gray-700 hover:bg-gray-100"
												>
													<span className="italic mr-1 text-xs sm:text-sm">
														Role Match:
													</span>
													<span className="text-xs sm:text-sm">
														{invite.role_relevance || 'N/A'}
													</span>
												</Badge>
											</div>
										)}
									</div>
									<CollapsibleTrigger asChild>
										<Button
											variant="ghost"
											size="sm"
											className="flex-shrink-0 md:visible hidden"
										>
											<ChevronDown className="h-4 w-4" />
											<span className="sr-only">Toggle email content</span>
										</Button>
									</CollapsibleTrigger>
								</div>
								<CollapsibleContent>
									<Card className="mt-2">
										<AIInviteSuggestion
											key={invite.id}
											invite={invite}
											onSend={handleInvite}
											onDiscard={handleInviteDiscard}
											setInvite={(invite) =>
												setInvite(invite as StrategyAILinkedinInvite)
											}
											disabled={isSendDisabled}
										/>
									</Card>
								</CollapsibleContent>
							</Collapsible>
						))}
						{aiEmails.length === 0 &&
							companies.length === 0 &&
							!isExecutingStrategy && (
								<div className="flex items-center justify-center h-32">
									<p className="text-muted-foreground">No emails available</p>
								</div>
							)}
						{aiEmails.map((email) => (
							<Collapsible
								key={email.id}
								open={expandedEmail === email.id}
								onOpenChange={() =>
									setExpandedEmail(expandedEmail === email.id ? null : email.id)
								}
							>
								<div
									onClick={(e) => {
										// Get closest target
										const target = e.target as HTMLElement;
										if (target.tagName === 'INPUT') return;
										setExpandedEmail(
											expandedEmail === email.id ? null : email.id
										);
									}}
									className="cursor-pointer flex p-4 items-center gap-4 flex-wrap rounded-lg border bg-card hover:bg-accent transition-colors"
								>
									<div className="flex-1">
										{email.company_title && email.company_description && (
											<div className="p-2 text-sm border-none rounded-md bg-muted">
												<span className="flex flex-col md:flex-row items-start md:items-center gap-2">
													<span className="flex gap-2 items-center">
														<Link2Icon className="h-4 w-4" />
														<Link
															className="underline font-semibold"
															href={`https://${email.email.split('@').pop()}`}
															target="_blank"
														>
															{email.email.split('@').pop()}
														</Link>
													</span>
													{email.company_title && (
														<h4 className="font-semibold">
															{email.company_title}
														</h4>
													)}
												</span>
											</div>
										)}
										<div className="flex items-center gap-2">
											<input
												type="checkbox"
												checked={selectedEmails.includes(email.id)}
												onChange={() => toggleEmailSelection(email.id)}
												className="h-4 w-4 rounded border-gray-300 sm:h-3 sm:w-3"
											/>
											{!isMobile && (
												<Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
											)}
											<span className="font-medium">
												<strong>{email.email}</strong>
												{` `}
												{email.subject}
											</span>
										</div>
										<h3 className="text-sm font-medium line-clamp-4 md:line-clamp-1">
											{email.body}
										</h3>
										{/* <div className="flex gap-2 mt-2">
											<span className="text-xs text-gray-500">Created At: {new Date(email.createdAt).toLocaleString()}</span>
											<span className="text-xs text-gray-500">Updated At: {new Date(email.updatedAt).toLocaleString()}</span>
										</div> */}
										{email.aiUpload && !isMobile && (
											<div className="flex gap-2 mt-2">
												<Link
													href={
														email.aiUploadId
															? `/ai-uploads/${email.aiUploadId}`
															: ''
													}
													target="_blank"
												>
													<Badge
														variant={'secondary'}
														className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
													>
														<span className="italic mr-1 text-xs sm:text-sm">
															{email.aiUpload?.rolePrompt} at
														</span>
														<span className="text-xs sm:text-sm">
															{email.aiUpload?.prompt}
														</span>
													</Badge>
												</Link>
												{email.company_relevance ? (
													<Badge
														variant={'secondary'}
														className="bg-teal-100 text-teal-700 hover:bg-teal-100"
													>
														<span className="italic mr-1 text-xs sm:text-sm">
															Search Score:
														</span>
														<span className="text-xs sm:text-sm">
															{email.company_relevance}
														</span>
													</Badge>
												) : null}
												<Badge
													variant={'secondary'}
													className="bg-gray-100 text-gray-700 hover:bg-gray-100"
												>
													<span className="italic mr-1 text-xs sm:text-sm">
														Role Match:
													</span>
													<span className="text-xs sm:text-sm">
														{email.role_relevance || 'N/A'}
													</span>
												</Badge>
											</div>
										)}
									</div>
									<CollapsibleTrigger asChild>
										<Button
											variant="ghost"
											size="sm"
											className="hidden md:block"
										>
											<ChevronDown
												className={`h-4 w-4 transition-transform duration-300 ${
													expandedEmail === email.id
														? 'rotate-180'
														: 'rotate-0'
												}`}
											/>
											<span className="sr-only">Toggle email content</span>
										</Button>
									</CollapsibleTrigger>
								</div>
								<CollapsibleContent>
									<Card className="mt-2">
										<AIEmailSuggestion
											key={email.id}
											email={email}
											onSend={handleSend}
											onDiscard={handleDiscard}
											onTestEmail={(email) => {
												setTestAIEmail(email);
												setIsTestEmailOpen(true);
											}}
											setEmail={(email) =>
												setAIEmail(email as StrategyAIEmail)
											}
											disabled={isSendDisabled}
										/>
									</Card>
								</CollapsibleContent>
							</Collapsible>
						))}
						{companies.map((company) => (
							<Collapsible key={company.domain} ref={refCompaniesContainer}>
								<div className="cursor-pointer flex p-4 items-center gap-4 flex-wrap rounded-lg border bg-card hover:bg-accent transition-colors">
									<div className="flex-1">
										{company.title && company.description && (
											<div className="p-2 text-sm border-none rounded-md bg-muted">
												<span className="flex flex-col md:flex-row items-start md:items-center gap-2">
													<span className="flex gap-2 items-center">
														<Link2Icon className="h-4 w-4" />
														<Link
															className="underline font-semibold"
															href={`https://${company.domain}`}
															target="_blank"
														>
															{company.domain}
														</Link>
													</span>
													{company.title && (
														<h4 className="font-semibold">
															{company.title}
														</h4>
													)}
												</span>
											</div>
										)}
										<div className="flex items-center gap-2">
											<input
												type="checkbox"
												disabled={true}
												className="h-4 w-4 rounded border-gray-300 sm:h-3 sm:w-3"
											/>
											{!isMobile && (
												<Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
											)}
											<span className="font-medium">
												<strong>
													{company.email ? (
														`${company.email.split('@')[0]}`
													) : (
														<span className="italic animate-pulse">
															loading
														</span>
													)}
													{`@${company.domain}`}
												</strong>
												{` `}
												{company.summary && company.summary}
											</span>
										</div>
										<h3 className="text-sm font-medium line-clamp-4 md:line-clamp-1 italic animate-pulse">
											{company.email
												? `Writing email subject & content...`
												: `Searching for employees...`}
										</h3>
										{company.aiUpload && !isMobile && (
											<div className="flex gap-2 mt-2">
												<Link
													href={
														company.aiUpload?.id
															? `/ai-uploads/${company.aiUpload.id}`
															: ''
													}
													target="_blank"
												>
													<Badge
														variant={'secondary'}
														className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
													>
														<span className="italic mr-1 text-xs sm:text-sm">
															{company.aiUpload?.rolePrompt} at
														</span>
														<span className="text-xs sm:text-sm">
															{company.aiUpload?.prompt}
														</span>
													</Badge>
												</Link>
												{company.relevance ? (
													<Badge
														variant={'secondary'}
														className="bg-teal-100 text-teal-700 hover:bg-teal-100"
													>
														<span className="italic mr-1 text-xs sm:text-sm">
															Search Score:
														</span>
														<span className="text-xs sm:text-sm">
															{company.relevance}
														</span>
													</Badge>
												) : null}
												{company.role_relevance ? (
													<Badge
														variant={'secondary'}
														className="bg-gray-100 text-gray-700 hover:bg-gray-100"
													>
														<span className="italic mr-1 text-xs sm:text-sm">
															Role Match:
														</span>
														<span className="text-xs sm:text-sm">
															{company.role_relevance || 'N/A'}
														</span>
													</Badge>
												) : null}
											</div>
										)}
									</div>
								</div>
							</Collapsible>
						))}
					</div>
				</div>

				<EmailSendModal
					isOpen={isEmailSendModalOpen}
					onClose={() => {
						setIsEmailSendModalOpen(false);
						setShouldAutoOpenEmailModal(false);
					}}
					onSend={() => sendAllEmails(selectedEmails.length > 0)}
					selectedCount={selectedEmails.length}
					totalCount={aiEmails.length + aiLinkedinInvites.length}
					isSelected={selectedEmails.length > 0}
					isEmailing={isEmailing}
					emailName={emailName}
					setEmailName={setEmailName}
					emailDomain={emailDomain}
					setEmailDomain={setEmailDomain}
					emailSignature={emailSignature}
					setEmailSignature={setEmailSignature}
					domains={domains}
					isSignedIn={!!session?.user?.email}
					shouldAutoOpen={shouldAutoOpenEmailModal}
				/>

				<TestEmailDialog
					isOpen={isTestEmailOpen}
					onOpenChange={setIsTestEmailOpen}
					onSendEmail={emailingTestCustomer}
					isEmailing={isEmailing}
				/>
			</div>
		);
	}
);

export default EmailStrategy;
